import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'calendar-timezone';

/** Returns the browser's local IANA timezone, e.g. "America/New_York" */
export const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Hook that provides the user's chosen timezone.
 * Stores 'auto' (browser default) or a specific IANA timezone in localStorage.
 */
export function useTimezone() {
  const [setting, setSetting] = useState<string>(() =>
    localStorage.getItem(STORAGE_KEY) || 'auto'
  );

  // The resolved IANA timezone string
  const timezone = setting === 'auto' ? getBrowserTimezone() : setting;

  const setTimezone = useCallback((value: string) => {
    setSetting(value);
    localStorage.setItem(STORAGE_KEY, value);
    // Notify other tabs / components
    window.dispatchEvent(new Event('timezone-changed'));
  }, []);

  // Listen for changes from other tabs or settings page
  useEffect(() => {
    const sync = () => {
      const stored = localStorage.getItem(STORAGE_KEY) || 'auto';
      if (stored !== setting) setSetting(stored);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('timezone-changed', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('timezone-changed', sync);
      window.removeEventListener('focus', sync);
    };
  }, [setting]);

  return { setting, timezone, setTimezone, isAuto: setting === 'auto' };
}

/**
 * Read-only accessor for the current timezone (no hook needed).
 * Useful in utility functions that can't use hooks.
 */
export function getTimezone(): string {
  const stored = localStorage.getItem(STORAGE_KEY) || 'auto';
  return stored === 'auto' ? getBrowserTimezone() : stored;
}
